// components/layout/Header/Header.tsx
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import useAuthStore from '@/features/auth/stores/auth.store';
import ProfileMenu from "./ProfileMenu";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { Search, X, Link as LinkIcon } from "lucide-react";
import { AddLinkForm } from "@/features/recent-links/components/AddLinkForm/AddLinkForm";
import type { Link as LinkType } from '@/features/recent-links/types';

interface HeaderProps {
  variant?: "default" | "transparent";
  onSearch?: (query: string) => void;
}

export function Header({ variant = "default" }: HeaderProps) {
  const isTransparent = variant === "transparent";
  const { isAuthenticated } = useAuthStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const navigate = useNavigate();
  
  // Add link form state
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const handleSearch = () => {
    if (searchTerm.trim()) {
      navigate(`/links?search=${encodeURIComponent(searchTerm.trim())}&limit=5`);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const handleLinkSuccess = (link: LinkType) => {
    // Navigate to links page to see the new link
    navigate('/links');
  };

  if (isTransparent) {
    // Landing page header
    return (
      <header className="fixed top-0 left-0 w-full z-50 bg-black/30 backdrop-blur-md border-b border-transparent">
        <div className="container mx-auto flex h-16 items-center justify-between px-4">
          {/* Logo */}
          <Link to="/" className="text-2xl font-bold text-white tracking-tight hover:opacity-90 transition-opacity">
            Link Library
          </Link>

          {/* Nav */}
          <nav className="hidden md:flex gap-6">
            <a href="#features" className="text-white/80 hover:text-white font-medium transition-colors">Features</a>
            <a href="#how-it-works" className="text-white/80 hover:text-white font-medium transition-colors">How It Works</a>
            <a href="#pricing" className="text-white/80 hover:text-white font-medium transition-colors">Pricing</a>
            <a href="#faq" className="text-white/80 hover:text-white font-medium transition-colors">FAQ</a>
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <Button asChild variant="outline" className="bg-transparent border-white text-white hover:bg-white/10 hover:text-white">
              <Link to="/auth/login">Login</Link>
            </Button>
            <Button asChild className="bg-white text-black hover:bg-gray-100">
              <Link to="/auth/register">Sign Up</Link>
            </Button>
          </div>
        </div>
      </header>
    );
  }

  // Default (app) header
  return (
    <header 
      className={`w-full border-b sticky top-0 z-50 bg-background/80 backdrop-blur-sm`}
    >
      <div className="flex h-16 items-center relative w-full">
        <div className="absolute left-0 ml-16">
          <span className="text-xl font-bold">
            Link Library
          </span>
        </div>
        {!isTransparent && isAuthenticated && (
          <div className="flex-grow flex justify-center mx-auto px-4 max-w-3xl">
            <div className="relative w-full flex items-center gap-2">
              <div 
                className={`flex items-center w-full border rounded-md transition-all ${
                  isFocused ? 'ring-2 ring-primary' : ''
                }`}
              >
                <div className="flex-shrink-0 pl-3">
                  <Search 
                    className="h-5 w-5 text-muted-foreground cursor-pointer" 
                    onClick={handleSearch}
                  />
                </div>
                <Input
                  type="text"
                  placeholder="Search ..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                  className="flex-grow border-0 focus-visible:ring-0 focus-visible:ring-offset-0 h-11"
                />
                {searchTerm && (
                  <div className="flex-shrink-0 pr-3">
                    <X 
                      className="h-5 w-5 text-muted-foreground cursor-pointer hover:text-foreground" 
                      onClick={clearSearch}
                    />
                  </div>
                )}
              </div>
              
              <Button
                variant="outline"
                size="icon"
                className="h-11 w-11 shrink-0 border rounded-md bg-background hover:bg-accent hover:text-accent-foreground"
                onClick={() => setIsEditDialogOpen(true)}
                title="Add new link"
              >
                <LinkIcon className="h-5 w-5" />
              </Button>
            </div>
          </div>
        )}
        <div className="absolute right-0 mr-2 flex items-center gap-4">
          {isAuthenticated && (
            <>
              <ProfileMenu />
              <ThemeToggle />
            </>
          )}
        </div>
      </div>

      {/* Use the reusable AddLinkForm component */}
      <AddLinkForm 
        isOpen={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        onSuccess={handleLinkSuccess}
      />
    </header>
  );
}

export default Header;